import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useMarket } from '@contexts/MarketContext';
import { useMarketApi } from '../hooks/useMarketApi';
import { useEffect, useState, useMemo, memo } from 'react';
import type { Bill, Invoice, Vendor, Customer } from '@lib/demo-data';

// PLAnalysisTable moved to Reports page only (MoE Recommendation: Single source of truth)

/**
 * Dashboard Page Component
 * @task B.INV.1 - Investor-grade dashboard with financial metrics widgets
 * @theme Polished Corporate - Teal accents, refined shadows, professional
 *
 * Main landing page with comprehensive financial overview including:
 * - Key financial KPIs with trends
 * - AI-powered automation metrics (70% touchless target)
 * - Cash flow projection chart
 * - AP/AR aging summaries
 * - Revenue trends
 * - AI insights and smart categorization
 * - Top customers and vendors
 * - Quick actions
 */

interface DashboardData {
  bills: Bill[];
  invoices: Invoice[];
  vendors: Vendor[];
  customers: Customer[];
}

export function Dashboard(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, formatCurrency, market, config, isBrazil } = useMarket();
  const { billsApi, invoicesApi, vendorsApi, customersApi } = useMarketApi();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [billsRes, invoicesRes, vendorsRes, customersRes] = await Promise.all([
          billsApi.getBills(),
          invoicesApi.getInvoices(),
          vendorsApi.getVendors(),
          customersApi.getCustomers(),
        ]);
        setData({
          bills: billsRes.data,
          invoices: invoicesRes.data,
          vendors: vendorsRes.data,
          customers: customersRes.data,
        });
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [market, billsApi, invoicesApi, vendorsApi, customersApi]);

  // Calculate metrics from demo data - memoized to prevent recalculation on every render
  const metrics = useMemo(() => {
    return data ? calculateMetrics(data) : null;
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 bg-gradient-to-b from-finova-teal-50/30 to-transparent p-4 sm:p-6">
      {/* Welcome Header - Polished Corporate - Mobile optimized */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
            {t('common.welcome')}, {user?.name?.split(' ')[0] ?? 'User'}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('dashboard.financialOverview')} {new Date().toLocaleDateString(config.locale, { month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => navigate('/accounts-payable/bills/new')}
            className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 sm:px-4 py-2.5 sm:py-2.5 text-sm font-medium shadow-card transition-all hover:shadow-card-hover hover:border-finova-teal-300 min-h-[44px]"
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden xs:inline">{t('dashboard.newBill')}</span>
            <span className="xs:hidden">{isBrazil ? 'Conta' : 'Bill'}</span>
          </button>
          <button
            onClick={() => navigate('/accounts-receivable/invoices/new')}
            className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 rounded-lg bg-finova-teal-600 px-3 sm:px-4 py-2.5 sm:py-2.5 text-sm font-medium text-white shadow-card transition-all hover:bg-finova-teal-700 hover:shadow-card-hover min-h-[44px]"
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden xs:inline">{t('dashboard.newInvoice')}</span>
            <span className="xs:hidden">{isBrazil ? 'NF' : 'Invoice'}</span>
          </button>
        </div>
      </div>

      {/* AI Automation Metrics Banner - Strategic Recommendation #2 */}
      <AIAutomationBanner t={t} isBrazil={isBrazil} />

      {/* Key Financial KPIs - Mobile: 2 cols, Tablet: 2 cols, Desktop: 4 cols */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <KPICard
          title={t('dashboard.totalRevenue')}
          value={metrics?.totalRevenue ?? 0}
          change={12.5}
          trend="up"
          icon={<TrendUpIcon className="h-5 w-5" />}
          color="green"
          formatCurrency={formatCurrency}
          vsLastMonth={t('dashboard.vsLastMonth')}
        />
        <KPICard
          title={t('dashboard.accountsReceivable')}
          value={metrics?.totalReceivables ?? 0}
          change={8.3}
          trend="up"
          icon={<InvoiceIcon className="h-5 w-5" />}
          color="blue"
          subtitle={`${metrics?.overdueInvoices ?? 0} ${t('dashboard.overdue')}`}
          formatCurrency={formatCurrency}
          vsLastMonth={t('dashboard.vsLastMonth')}
        />
        <KPICard
          title={t('dashboard.accountsPayable')}
          value={metrics?.totalPayables ?? 0}
          change={-5.2}
          trend="down"
          icon={<BillIcon className="h-5 w-5" />}
          color="orange"
          subtitle={`${metrics?.overdueBills ?? 0} ${t('dashboard.overdue')}`}
          formatCurrency={formatCurrency}
          vsLastMonth={t('dashboard.vsLastMonth')}
        />
        <KPICard
          title={t('dashboard.netCashPosition')}
          value={metrics?.cashPosition ?? 0}
          change={15.7}
          trend="up"
          icon={<CashIcon className="h-5 w-5" />}
          color="purple"
          formatCurrency={formatCurrency}
          vsLastMonth={t('dashboard.vsLastMonth')}
        />
      </div>

      {/* Main Content Grid - Mobile: stack, Tablet: 2-col, Desktop: 3-col */}
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Cash Flow Summary Card - Links to detailed report (MoE Recommendation) */}
        <div className="md:col-span-2 lg:col-span-2">
          <CashFlowSummaryCard
            t={t}
            formatCurrency={formatCurrency}
            isBrazil={isBrazil}
            onViewDetails={() => navigate('/reports')}
          />
        </div>

        {/* AP/AR Summary - Takes 1 column on lg, full width on tablet */}
        <div className="space-y-4 sm:space-y-6 md:col-span-2 lg:col-span-1">
          <AgingSummary
            title={t('dashboard.receivablesAging')}
            data={metrics?.arAging ?? { current: 0, days30: 0, days60: 0, days90: 0 }}
            formatCurrency={formatCurrency}
            t={t}
          />
          <AgingSummary
            title={t('dashboard.payablesAging')}
            data={metrics?.apAging ?? { current: 0, days30: 0, days60: 0, days90: 0 }}
            formatCurrency={formatCurrency}
            t={t}
          />
        </div>
      </div>

      {/* Revenue & Financial Modules - Mobile: stack, Tablet+: 2-col */}
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
        {/* Revenue Trend */}
        <RevenueTrendChart t={t} formatCurrency={formatCurrency} isBrazil={isBrazil} />

        {/* Financial Modules Quick Access */}
        <div className="rounded-xl bg-white p-4 sm:p-6 shadow-card">
          <h3 className="mb-3 sm:mb-4 text-base sm:text-lg font-semibold text-gray-900">{t('dashboard.financialModules')}</h3>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <ModuleQuickLink
              title={t('dashboard.generalLedger')}
              description={t('dashboard.chartOfAccounts')}
              icon={<LedgerIcon className="h-5 w-5" />}
              path="/general-ledger"
              color="blue"
              stat={isBrazil ? '156 contas' : '156 accounts'}
            />
            <ModuleQuickLink
              title={isBrazil ? 'Contas a Pagar' : 'Accounts Payable'}
              description={isBrazil ? 'Faturas de fornecedores' : 'Vendor bills'}
              icon={<BillIcon className="h-5 w-5" />}
              path="/accounts-payable/bills"
              color="red"
              stat={`${metrics?.pendingBills ?? 0} ${t('dashboard.pending')}`}
            />
            <ModuleQuickLink
              title={isBrazil ? 'Contas a Receber' : 'Accounts Receivable'}
              description={isBrazil ? 'NF-e de clientes' : 'Customer invoices'}
              icon={<InvoiceIcon className="h-5 w-5" />}
              path="/accounts-receivable/invoices"
              color="green"
              stat={`${metrics?.openInvoices ?? 0} ${t('dashboard.open')}`}
            />
            <ModuleQuickLink
              title={t('dashboard.cashBanking')}
              description={t('dashboard.bankReconciliation')}
              icon={<BankIcon className="h-5 w-5" />}
              path="/cash-banking"
              color="purple"
              stat={isBrazil ? '3 contas' : '3 accounts'}
            />
          </div>
        </div>
      </div>

      {/* P&L Analysis moved to Reports > DRE tab (MoE Recommendation) */}

      {/* Top Customers & Vendors - Mobile: stack, Tablet+: 2-col */}
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
        <TopList
          title={t('dashboard.topCustomers')}
          subtitle={t('dashboard.byOutstandingBalance')}
          items={metrics?.topCustomers ?? []}
          onViewAll={() => navigate('/accounts-receivable/customers')}
          type="customer"
          formatCurrency={formatCurrency}
          isBrazil={isBrazil}
          viewAllText={t('common.viewAll')}
        />
        <TopList
          title={t('dashboard.topVendors')}
          subtitle={t('dashboard.byOutstandingBalance')}
          items={metrics?.topVendors ?? []}
          onViewAll={() => navigate('/accounts-payable/vendors')}
          type="vendor"
          formatCurrency={formatCurrency}
          isBrazil={isBrazil}
          viewAllText={t('common.viewAll')}
        />
      </div>

      {/* Recent Activity */}
      <RecentActivitySection t={t} isBrazil={isBrazil} formatCurrency={formatCurrency} />
    </div>
  );
}

// ============================================================================
// Metrics Calculation
// ============================================================================

interface Metrics {
  totalRevenue: number;
  totalReceivables: number;
  totalPayables: number;
  cashPosition: number;
  overdueInvoices: number;
  overdueBills: number;
  pendingBills: number;
  openInvoices: number;
  arAging: { current: number; days30: number; days60: number; days90: number };
  apAging: { current: number; days30: number; days60: number; days90: number };
  topCustomers: Array<{ name: string; amount: number; count: number }>;
  topVendors: Array<{ name: string; amount: number; count: number }>;
}

function calculateMetrics(data: DashboardData): Metrics {
  const { bills, invoices } = data;

  // Calculate receivables
  const openInvoices = invoices.filter((i) => !['paid', 'void'].includes(i.status));
  const totalReceivables = openInvoices.reduce((sum, i) => sum + (i.amount - i.paidAmount), 0);
  const overdueInvoices = openInvoices.filter((i) => i.status === 'overdue').length;

  // Calculate payables
  const openBills = bills.filter((b) => !['paid'].includes(b.status));
  const totalPayables = openBills.reduce((sum, b) => sum + b.amount, 0);
  const overdueBills = openBills.filter((b) => b.status === 'overdue').length;
  const pendingBills = openBills.filter((b) => b.status === 'pending').length;

  // Calculate aging (simplified)
  const today = new Date();
  const arAging = { current: 0, days30: 0, days60: 0, days90: 0 };
  const apAging = { current: 0, days30: 0, days60: 0, days90: 0 };

  openInvoices.forEach((inv) => {
    const dueDate = new Date(inv.dueDate);
    const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
    const amount = inv.amount - inv.paidAmount;
    if (daysOverdue <= 0) arAging.current += amount;
    else if (daysOverdue <= 30) arAging.days30 += amount;
    else if (daysOverdue <= 60) arAging.days60 += amount;
    else arAging.days90 += amount;
  });

  openBills.forEach((bill) => {
    const dueDate = new Date(bill.dueDate);
    const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysOverdue <= 0) apAging.current += bill.amount;
    else if (daysOverdue <= 30) apAging.days30 += bill.amount;
    else if (daysOverdue <= 60) apAging.days60 += bill.amount;
    else apAging.days90 += bill.amount;
  });

  // Top customers by outstanding
  const customerTotals = new Map<string, { name: string; amount: number; count: number }>();
  openInvoices.forEach((inv) => {
    const existing = customerTotals.get(inv.customerId) ?? { name: inv.customerName, amount: 0, count: 0 };
    existing.amount += inv.amount - inv.paidAmount;
    existing.count += 1;
    customerTotals.set(inv.customerId, existing);
  });
  const topCustomers = Array.from(customerTotals.values())
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  // Top vendors by outstanding
  const vendorTotals = new Map<string, { name: string; amount: number; count: number }>();
  openBills.forEach((bill) => {
    const existing = vendorTotals.get(bill.vendorId) ?? { name: bill.vendorName, amount: 0, count: 0 };
    existing.amount += bill.amount;
    existing.count += 1;
    vendorTotals.set(bill.vendorId, existing);
  });
  const topVendors = Array.from(vendorTotals.values())
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  // Monthly revenue - sum of paid invoices for current month
  // The invoice data is already market-aware (US uses USD amounts, BR uses BRL amounts)
  const totalRevenue = invoices
    .filter((i) => i.status === 'paid')
    .reduce((sum, i) => sum + i.amount, 0);

  // Cash position
  const cashPosition = 342890.50 + totalRevenue * 0.3 - totalPayables * 0.5;

  return {
    totalRevenue,
    totalReceivables,
    totalPayables,
    cashPosition,
    overdueInvoices,
    overdueBills,
    pendingBills,
    openInvoices: openInvoices.length,
    arAging,
    apAging,
    topCustomers,
    topVendors,
  };
}

// ============================================================================
// KPI Card Component
// ============================================================================

interface KPICardProps {
  title: string;
  value: number;
  change: number;
  trend: 'up' | 'down';
  icon: React.ReactNode;
  color: 'green' | 'blue' | 'orange' | 'purple';
  subtitle?: string;
  formatCurrency: (amount: number) => string;
  vsLastMonth: string;
}

// Polished Corporate Color Classes - Teal-based palette
const colorClasses = {
  green: 'bg-emerald-50 text-emerald-600',
  blue: 'bg-finova-cyan-50 text-finova-cyan-600',
  orange: 'bg-amber-50 text-amber-600',
  purple: 'bg-violet-50 text-violet-600',
  red: 'bg-rose-50 text-rose-600',
  teal: 'bg-finova-teal-50 text-finova-teal-600',
};

const KPICard = memo(function KPICard({ title, value, change, trend, icon, color, subtitle, formatCurrency, vsLastMonth }: KPICardProps): JSX.Element {
  const isPositive = trend === 'up' && change > 0 || trend === 'down' && change < 0;

  return (
    <div className="rounded-xl bg-white p-4 sm:p-6 shadow-card transition-all hover:shadow-card-hover">
      <div className="flex items-center justify-between">
        <p className="text-xs sm:text-sm font-medium text-gray-500 truncate pr-2">{title}</p>
        <div className={`rounded-lg sm:rounded-xl p-2 sm:p-2.5 ${colorClasses[color]} flex-shrink-0`}>
          {icon}
        </div>
      </div>
      <div className="mt-3 sm:mt-4">
        <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatCurrency(value)}</p>
        <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-1 sm:gap-2">
          <span className={`inline-flex items-center rounded-full px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium ${
            isPositive ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
          }`}>
            {trend === 'up' ? (
              <ArrowUpIcon className="mr-0.5 sm:mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />
            ) : (
              <ArrowDownIcon className="mr-0.5 sm:mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />
            )}
            {Math.abs(change)}%
          </span>
          <span className="text-[10px] sm:text-xs text-gray-400 hidden xs:inline">{vsLastMonth}</span>
        </div>
        {subtitle && (
          <p className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs font-medium text-amber-600">{subtitle}</p>
        )}
      </div>
    </div>
  );
});

// ============================================================================
// Cash Flow Summary Card Component (MoE Recommendation: Summary + Link to Report)
// ============================================================================

interface CashFlowSummaryCardProps {
  t: (key: string) => string;
  formatCurrency: (amount: number) => string;
  isBrazil: boolean;
  onViewDetails: () => void;
}

const CashFlowSummaryCard = memo(function CashFlowSummaryCard({ t, formatCurrency, isBrazil, onViewDetails }: CashFlowSummaryCardProps): JSX.Element {
  // Monthly data aligned with CashFlowReport (Fluxo de Caixa)
  const inflows = isBrazil
    ? [856000, 977000, 1437500, 1143250, 1309000, 1450000]
    : [171200, 195350, 287500, 228650, 261800, 290000];
  const outflows = isBrazil
    ? [557500, 494000, 498500, 885250, 835500, 557000]
    : [111500, 98800, 99700, 177050, 167100, 111400];

  const totalInflows = inflows.reduce((a, b) => a + b, 0);
  const totalOutflows = outflows.reduce((a, b) => a + b, 0);
  const netFlow = totalInflows - totalOutflows;

  // Calculate trend (comparing last 3 months vs previous 3 months)
  const recentNet = (inflows.slice(-3).reduce((a, b) => a + b, 0) - outflows.slice(-3).reduce((a, b) => a + b, 0));
  const previousNet = (inflows.slice(0, 3).reduce((a, b) => a + b, 0) - outflows.slice(0, 3).reduce((a, b) => a + b, 0));
  const trendPercent = previousNet > 0 ? ((recentNet - previousNet) / previousNet * 100).toFixed(1) : '0';
  const trendUp = recentNet >= previousNet;

  const months = isBrazil ? ['Ago', 'Set', 'Out', 'Nov', 'Dez', 'Jan'] : ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
  // Bar chart height - responsive via CSS, base height for calculations
  const barMaxHeight = 100; // pixels - slightly smaller for better mobile fit

  // Format compact currency for large numbers
  const formatCompact = (amount: number): string => {
    if (isBrazil) {
      if (amount >= 1000000) return `R$${(amount / 1000000).toFixed(1)}M`;
      if (amount >= 1000) return `R$${(amount / 1000).toFixed(0)}K`;
      return `R$${amount.toFixed(0)}`;
    }
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  return (
    <div className="rounded-xl bg-white p-4 sm:p-6 shadow-card">
      {/* Header */}
      <div className="mb-4 sm:mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">{t('dashboard.cashFlow')}</h3>
          <p className="text-xs sm:text-sm text-gray-500">{t('dashboard.sixMonthOverview')}</p>
        </div>
        <button
          onClick={onViewDetails}
          className="inline-flex items-center gap-1.5 rounded-lg bg-finova-teal-50 px-3 py-1.5 text-xs sm:text-sm font-medium text-finova-teal-600 hover:bg-finova-teal-100 transition-colors self-start sm:self-auto"
        >
          {isBrazil ? 'Ver Relatório Completo' : 'View Full Report'}
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Main Metrics Grid - Compact currency for overflow prevention */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 mb-4 sm:mb-6">
        {/* Total Inflows */}
        <div className="rounded-lg bg-emerald-50 p-3 sm:p-4 overflow-hidden">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />
            <p className="text-[9px] sm:text-xs font-medium uppercase tracking-wide text-emerald-600 truncate">{t('dashboard.totalInflows')}</p>
          </div>
          <p className="text-base sm:text-lg font-bold text-emerald-700 truncate" title={formatCurrency(totalInflows)}>
            {formatCompact(totalInflows)}
          </p>
        </div>

        {/* Total Outflows */}
        <div className="rounded-lg bg-rose-50 p-3 sm:p-4 overflow-hidden">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="h-2 w-2 rounded-full bg-rose-500 flex-shrink-0" />
            <p className="text-[9px] sm:text-xs font-medium uppercase tracking-wide text-rose-600 truncate">{t('dashboard.totalOutflows')}</p>
          </div>
          <p className="text-base sm:text-lg font-bold text-rose-700 truncate" title={formatCurrency(totalOutflows)}>
            {formatCompact(totalOutflows)}
          </p>
        </div>

        {/* Net Cash Flow */}
        <div className="rounded-lg bg-finova-teal-50 p-3 sm:p-4 overflow-hidden">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="h-2 w-2 rounded-full bg-finova-teal-500 flex-shrink-0" />
            <p className="text-[9px] sm:text-xs font-medium uppercase tracking-wide text-finova-teal-600 truncate">{t('dashboard.netCashFlow')}</p>
          </div>
          <p className="text-base sm:text-lg font-bold text-finova-teal-700 truncate" title={formatCurrency(netFlow)}>
            {formatCompact(netFlow)}
          </p>
        </div>

        {/* Trend */}
        <div className="rounded-lg bg-violet-50 p-3 sm:p-4 overflow-hidden">
          <div className="flex items-center gap-1.5 mb-1">
            <div className={`h-2 w-2 rounded-full flex-shrink-0 ${trendUp ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            <p className="text-[9px] sm:text-xs font-medium uppercase tracking-wide text-violet-600 truncate">{isBrazil ? 'Tendência' : 'Trend'}</p>
          </div>
          <div className="flex items-center gap-1">
            <p className={`text-base sm:text-lg font-bold ${trendUp ? 'text-emerald-600' : 'text-rose-600'}`}>
              {trendUp ? '+' : ''}{trendPercent}%
            </p>
            <svg className={`h-4 w-4 flex-shrink-0 ${trendUp ? 'text-emerald-500' : 'text-rose-500 rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </div>
        </div>
      </div>

      {/* Bar Chart - Inflows (green) and Outflows (red) */}
      <div className="rounded-lg bg-gray-50 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs sm:text-sm font-medium text-gray-600">{isBrazil ? 'Entradas vs Saídas' : 'Inflows vs Outflows'}</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <span className="text-[10px] sm:text-xs text-gray-500">{isBrazil ? 'Entradas' : 'Inflows'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-rose-400" />
              <span className="text-[10px] sm:text-xs text-gray-500">{isBrazil ? 'Saídas' : 'Outflows'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-end gap-3 sm:gap-5" style={{ height: `${barMaxHeight + 28}px` }}>
          {inflows.map((inflow, i) => {
            const outflow = outflows[i] ?? 0;
            const maxValue = Math.max(...inflows, ...outflows);
            const inflowHeight = Math.max(Math.round((inflow / maxValue) * barMaxHeight), 8);
            const outflowHeight = Math.max(Math.round((outflow / maxValue) * barMaxHeight), 8);
            return (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div className="flex items-end gap-1 w-full justify-center">
                  <div
                    className="w-[40%] max-w-[28px] rounded-t-md bg-emerald-400 hover:bg-emerald-500 transition-colors cursor-pointer"
                    style={{ height: `${inflowHeight}px` }}
                    title={`${isBrazil ? 'Entrada' : 'Inflow'}: ${formatCurrency(inflow)}`}
                  />
                  <div
                    className="w-[40%] max-w-[28px] rounded-t-md bg-rose-400 hover:bg-rose-500 transition-colors cursor-pointer"
                    style={{ height: `${outflowHeight}px` }}
                    title={`${isBrazil ? 'Saída' : 'Outflow'}: ${formatCurrency(outflow)}`}
                  />
                </div>
                <span className="text-[10px] sm:text-xs text-gray-500 mt-2 font-medium">{months[i]}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// Revenue Trend Chart Component
// ============================================================================

interface RevenueTrendChartProps {
  t: (key: string) => string;
  formatCurrency: (amount: number) => string;
  isBrazil: boolean;
}

const RevenueTrendChart = memo(function RevenueTrendChart({ t, formatCurrency: _formatCurrency, isBrazil }: RevenueTrendChartProps): JSX.Element {
  // Revenue data aligned with Cash Flow inflows for consistency
  const data = isBrazil
    ? [
        { month: 'Ago', revenue: 856000, target: 800000 },
        { month: 'Set', revenue: 977000, target: 900000 },
        { month: 'Out', revenue: 1437500, target: 1000000 },
        { month: 'Nov', revenue: 1143250, target: 1100000 },
        { month: 'Dez', revenue: 1309000, target: 1200000 },
        { month: 'Jan', revenue: 1450000, target: 1300000 },
      ]
    : [
        { month: 'Aug', revenue: 171200, target: 160000 },
        { month: 'Sep', revenue: 195350, target: 180000 },
        { month: 'Oct', revenue: 287500, target: 200000 },
        { month: 'Nov', revenue: 228650, target: 220000 },
        { month: 'Dec', revenue: 261800, target: 240000 },
        { month: 'Jan', revenue: 290000, target: 260000 },
      ];
  const maxRevenue = Math.max(...data.map((d) => Math.max(d.revenue, d.target)));

  return (
    <div className="rounded-xl bg-white p-4 sm:p-6 shadow-card">
      <div className="mb-4 sm:mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">{t('dashboard.revenueTrend')}</h3>
          <p className="text-xs sm:text-sm text-gray-500">{t('dashboard.actualVsTarget')}</p>
        </div>
        <div className="rounded-lg bg-emerald-50 px-2 sm:px-3 py-1.5 sm:py-2 text-right self-start sm:self-auto">
          <p className="text-lg sm:text-xl font-bold text-emerald-600">+20%</p>
          <p className="text-[10px] sm:text-xs text-emerald-600/70">{t('dashboard.aboveTarget')}</p>
        </div>
      </div>

      {/* Line Chart (simplified with bars) - Responsive */}
      <div className="flex h-[140px] sm:h-[180px] items-end gap-1.5 sm:gap-3">
        {data.map((item) => (
          <div key={item.month} className="flex flex-1 flex-col items-center gap-1 sm:gap-2">
            <div className="relative flex w-full flex-col items-center gap-1">
              {/* Revenue bar */}
              <div
                className="w-5 sm:w-10 rounded-t-md sm:rounded-t-lg bg-gradient-to-t from-finova-teal-600 to-finova-teal-400 transition-all hover:from-finova-teal-500 hover:to-finova-teal-300"
                style={{ height: `${(item.revenue / maxRevenue) * 110}px` }}
              />
              {/* Target line indicator */}
              <div
                className="absolute left-0 right-0 border-t-2 border-dashed border-amber-400"
                style={{ bottom: `${(item.target / maxRevenue) * 110}px` }}
              />
            </div>
            <span className="text-[10px] sm:text-xs font-medium text-gray-400">{item.month}</span>
          </div>
        ))}
      </div>

      {/* Legend - Responsive */}
      <div className="mt-3 sm:mt-4 flex justify-center gap-4 sm:gap-6">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="h-2.5 w-2.5 sm:h-3 sm:w-3 rounded bg-finova-teal-500" />
          <span className="text-xs sm:text-sm text-gray-500">{t('dashboard.actualRevenue')}</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="h-0.5 w-3 sm:w-4 border-t-2 border-dashed border-amber-400" />
          <span className="text-xs sm:text-sm text-gray-500">{t('dashboard.target')}</span>
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// Aging Summary Component
// ============================================================================

interface AgingSummaryProps {
  title: string;
  data: { current: number; days30: number; days60: number; days90: number };
  formatCurrency: (amount: number) => string;
  t: (key: string) => string;
}

const AgingSummary = memo(function AgingSummary({ title, data, formatCurrency, t }: AgingSummaryProps): JSX.Element {
  const total = data.current + data.days30 + data.days60 + data.days90;
  const items = [
    { label: t('dashboard.current'), value: data.current, color: 'bg-emerald-500', textColor: 'text-emerald-600' },
    { label: t('dashboard.days30'), value: data.days30, color: 'bg-amber-400', textColor: 'text-amber-600' },
    { label: t('dashboard.days60'), value: data.days60, color: 'bg-orange-500', textColor: 'text-orange-600' },
    { label: t('dashboard.days90'), value: data.days90, color: 'bg-rose-500', textColor: 'text-rose-600' },
  ];

  return (
    <div className="rounded-xl bg-white p-4 sm:p-5 shadow-card">
      <div className="mb-3 sm:mb-4 flex items-center justify-between">
        <h4 className="text-sm sm:text-base font-semibold text-gray-900">{title}</h4>
        <span className="text-xs sm:text-sm font-bold text-finova-teal-600">{formatCurrency(total)}</span>
      </div>

      {/* Progress bar */}
      <div className="mb-3 sm:mb-4 flex h-2 sm:h-2.5 overflow-hidden rounded-full bg-gray-100">
        {items.map((item) => (
          item.value > 0 && (
            <div
              key={item.label}
              className={`${item.color} transition-all`}
              style={{ width: `${(item.value / total) * 100}%` }}
            />
          )
        ))}
      </div>

      {/* Breakdown - Responsive text */}
      <div className="space-y-2 sm:space-y-2.5">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between text-xs sm:text-sm">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full ${item.color}`} />
              <span className="text-gray-500">{item.label}</span>
            </div>
            <span className={`font-semibold ${item.textColor}`}>{formatCurrency(item.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

// ============================================================================
// Module Quick Link Component
// ============================================================================

interface ModuleQuickLinkProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: 'blue' | 'red' | 'green' | 'purple' | 'orange';
  stat: string;
}

const ModuleQuickLink = memo(function ModuleQuickLink({ title, description, icon, path, color, stat }: ModuleQuickLinkProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(path)}
      className="flex items-start gap-2 sm:gap-3 rounded-lg sm:rounded-xl bg-gray-50 p-3 sm:p-4 text-left transition-all hover:bg-finova-teal-50 hover:shadow-sm group min-h-[44px]"
    >
      <div className={`rounded-lg sm:rounded-xl p-2 sm:p-2.5 ${colorClasses[color]} transition-colors group-hover:bg-finova-teal-100 group-hover:text-finova-teal-600 flex-shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{title}</p>
        <p className="text-[10px] sm:text-xs text-gray-500 truncate hidden xs:block">{description}</p>
        <p className="mt-1 sm:mt-1.5 text-[10px] sm:text-xs font-semibold text-finova-teal-600">{stat}</p>
      </div>
    </button>
  );
});

// ============================================================================
// Top List Component
// ============================================================================

interface TopListProps {
  title: string;
  subtitle: string;
  items: Array<{ name: string; amount: number; count: number }>;
  onViewAll: () => void;
  type: 'customer' | 'vendor';
  formatCurrency: (amount: number) => string;
  isBrazil: boolean;
  viewAllText: string;
}

const TopList = memo(function TopList({ title, subtitle, items, onViewAll, type, formatCurrency, isBrazil, viewAllText }: TopListProps): JSX.Element {
  const getCountLabel = (count: number) => {
    if (isBrazil) {
      return type === 'customer'
        ? `${count} ${count === 1 ? 'NF-e' : 'NF-es'}`
        : `${count} ${count === 1 ? 'fatura' : 'faturas'}`;
    }
    return `${count} ${type === 'customer' ? 'invoice' : 'bill'}${count !== 1 ? 's' : ''}`;
  };

  const getEmptyMessage = () => {
    if (isBrazil) {
      return type === 'customer' ? 'Nenhum cliente pendente' : 'Nenhum fornecedor pendente';
    }
    return `No outstanding ${type}s`;
  };

  return (
    <div className="rounded-xl bg-white p-4 sm:p-6 shadow-card">
      <div className="mb-3 sm:mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm sm:text-base font-semibold text-gray-900">{title}</h3>
          <p className="text-xs sm:text-sm text-gray-500">{subtitle}</p>
        </div>
        <button
          onClick={onViewAll}
          className="text-xs sm:text-sm font-semibold text-finova-teal-600 hover:text-finova-teal-700 transition-colors min-h-[44px] px-2"
        >
          {viewAllText}
        </button>
      </div>

      {items.length === 0 ? (
        <p className="py-6 sm:py-8 text-center text-gray-400 text-sm">{getEmptyMessage()}</p>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {items.map((item, index) => {
            // Truncate long names on mobile (max 18 chars)
            const displayName = item.name.length > 18
              ? `${item.name.substring(0, 18)}...`
              : item.name;
            return (
              <div key={item.name} className="flex items-center gap-2 sm:gap-3 rounded-lg p-1.5 sm:p-2 transition-colors hover:bg-gray-50">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-finova-teal-100 text-xs sm:text-sm font-bold text-finova-teal-600 flex-shrink-0">
                  {index + 1}
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 truncate sm:text-clip" title={item.name}>
                    <span className="sm:hidden">{displayName}</span>
                    <span className="hidden sm:inline">{item.name}</span>
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-500">{getCountLabel(item.count)}</p>
                </div>
                <p className="text-xs sm:text-sm font-bold text-gray-900 flex-shrink-0 whitespace-nowrap">{formatCurrency(item.amount)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ============================================================================
// Recent Activity Section
// ============================================================================

interface RecentActivitySectionProps {
  t: (key: string) => string;
  isBrazil: boolean;
  formatCurrency: (amount: number) => string;
}

const RecentActivitySection = memo(function RecentActivitySection({ t, isBrazil, formatCurrency }: RecentActivitySectionProps): JSX.Element {
  // Market-aware recent activity for demo - includes AI-processed items
  const activities = isBrazil
    ? [
        {
          type: 'ai_categorized',
          title: 'Recibo categorizado automaticamente',
          description: `Despesa de material de escritório - ${formatCurrency(1175.50)}`,
          timestamp: 'Há 15 min',
          icon: <SparklesIcon className="h-4 w-4" />,
          iconColor: 'bg-violet-100 text-violet-600',
          isAI: true,
        },
        {
          type: 'invoice_created',
          title: 'Nova NF-e criada',
          description: 'NF-e #2026-0042 para TechBrasil Soluções',
          timestamp: 'Há 2 horas',
          icon: <PlusIcon className="h-4 w-4" />,
          iconColor: 'bg-emerald-50 text-emerald-600',
        },
        {
          type: 'ai_extracted',
          title: 'Dados da fatura extraídos automaticamente',
          description: 'Fatura da LogiTech LTDA - preenchimento via PDF',
          timestamp: 'Há 3 horas',
          icon: <SparklesIcon className="h-4 w-4" />,
          iconColor: 'bg-violet-100 text-violet-600',
          isAI: true,
        },
        {
          type: 'payment_received',
          title: 'Pagamento recebido via PIX',
          description: `Varejo Pacífico LTDA pagou ${formatCurrency(44500.00)}`,
          timestamp: 'Ontem',
          icon: <CashIcon className="h-4 w-4" />,
          iconColor: 'bg-finova-teal-50 text-finova-teal-600',
        },
      ]
    : [
        {
          type: 'ai_categorized',
          title: 'Auto-categorized receipt',
          description: `Office supplies expense from Staples - ${formatCurrency(234.50)}`,
          timestamp: '15 min ago',
          icon: <SparklesIcon className="h-4 w-4" />,
          iconColor: 'bg-violet-100 text-violet-600',
          isAI: true,
        },
        {
          type: 'invoice_created',
          title: 'New invoice created',
          description: 'Invoice #INV-2026-0042 for Global Tech Industries',
          timestamp: '2 hours ago',
          icon: <PlusIcon className="h-4 w-4" />,
          iconColor: 'bg-emerald-50 text-emerald-600',
        },
        {
          type: 'ai_extracted',
          title: 'Bill data extracted',
          description: 'Bill from Tech Solutions LLC - auto-filled from PDF',
          timestamp: '3 hours ago',
          icon: <SparklesIcon className="h-4 w-4" />,
          iconColor: 'bg-violet-100 text-violet-600',
          isAI: true,
        },
        {
          type: 'payment_received',
          title: 'Payment received',
          description: `Pacific Retail Group paid ${formatCurrency(8900.00)}`,
          timestamp: 'Yesterday',
          icon: <CashIcon className="h-4 w-4" />,
          iconColor: 'bg-finova-teal-50 text-finova-teal-600',
        },
      ];

  return (
    <div className="rounded-xl bg-white shadow-card">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 sm:px-6 py-3 sm:py-4">
        <h3 className="text-sm sm:text-base font-semibold text-gray-900">{t('dashboard.recentActivity')}</h3>
        <span className="rounded-full bg-violet-100 px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-violet-600">
          {t('dashboard.aiPowered')}
        </span>
      </div>
      <div className="divide-y divide-gray-50">
        {activities.map((activity, index) => (
          <div key={index} className="flex items-start gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 transition-colors hover:bg-gray-50">
            <div className={`flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-lg sm:rounded-xl ${activity.iconColor} flex-shrink-0`}>
              {activity.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                <p className="text-sm sm:text-base font-medium text-gray-900">{activity.title}</p>
                {activity.isAI && (
                  <span className="rounded bg-violet-100 px-1 sm:px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase text-violet-600">
                    {isBrazil ? 'IA' : 'AI'}
                  </span>
                )}
              </div>
              <p className="truncate text-xs sm:text-sm text-gray-500">{activity.description}</p>
            </div>
            <p className="whitespace-nowrap text-[10px] sm:text-xs font-medium text-gray-400 flex-shrink-0">{activity.timestamp}</p>
          </div>
        ))}
      </div>
    </div>
  );
});

// ============================================================================
// AI Automation Banner - Strategic Recommendations #1, #2 (Collapsible)
// ============================================================================

interface AIAutomationBannerProps {
  t: (key: string) => string;
  isBrazil: boolean;
}

const AIAutomationBanner = memo(function AIAutomationBanner({ t, isBrazil }: AIAutomationBannerProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(true);
  const automationMetrics = isBrazil
    ? [
        {
          label: 'Proc. Automático',
          labelFull: 'Processamento Automático',
          value: 72,
          target: 70,
          icon: <SparklesIcon className="h-4 w-4" />,
          description: 'Transações automatizadas',
          onTarget: 'Meta',
          targetLabel: 'Meta',
        },
        {
          label: 'Categorização IA',
          labelFull: 'Categorização por IA',
          value: 96,
          target: 95,
          icon: <BrainIcon className="h-4 w-4" />,
          description: 'Taxa de precisão',
          onTarget: 'Meta',
          targetLabel: 'Meta',
        },
        {
          label: 'Proc. Recibos',
          labelFull: 'Processamento de Recibos',
          value: 3.2,
          target: 5,
          unit: 's',
          icon: <ScanIcon className="h-4 w-4" />,
          description: 'Tempo médio de extração',
          onTarget: 'Meta',
          targetLabel: 'Meta',
        },
        {
          label: 'Extração NF-e',
          labelFull: 'Extração de NF-e',
          value: 98,
          target: 98,
          icon: <DocumentIcon className="h-4 w-4" />,
          description: 'Precisão de dados',
          onTarget: 'Meta',
          targetLabel: 'Meta',
        },
      ]
    : [
        {
          label: 'Touchless',
          labelFull: 'Touchless Processing',
          value: 72,
          target: 70,
          icon: <SparklesIcon className="h-4 w-4" />,
          description: 'Auto-processed',
          onTarget: 'Target',
          targetLabel: 'Target',
        },
        {
          label: 'AI Categ.',
          labelFull: 'AI Categorization',
          value: 96,
          target: 95,
          icon: <BrainIcon className="h-4 w-4" />,
          description: 'Accuracy',
          onTarget: 'Target',
          targetLabel: 'Target',
        },
        {
          label: 'Receipt',
          labelFull: 'Receipt Processing',
          value: 3.2,
          target: 5,
          unit: 's',
          icon: <ScanIcon className="h-4 w-4" />,
          description: 'Avg. time',
          onTarget: 'Target',
          targetLabel: 'Target',
        },
        {
          label: 'Invoice',
          labelFull: 'Invoice Extraction',
          value: 98,
          target: 98,
          icon: <DocumentIcon className="h-4 w-4" />,
          description: 'Accuracy',
          onTarget: 'Target',
          targetLabel: 'Target',
        },
      ];

  return (
    <div className="rounded-xl sm:rounded-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-finova-teal-600 p-[1px]">
      <div className="rounded-xl sm:rounded-2xl bg-white p-3 sm:p-5">
        {/* Header - Responsive with Collapse Toggle */}
        <div
          className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex-shrink-0">
              <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">{t('ai.automation')}</h3>
              <p className="text-xs sm:text-sm text-gray-500 hidden xs:block">{t('ai.realTimeMetrics')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 self-start sm:self-auto">
            <div className="flex items-center gap-1.5 sm:gap-2 rounded-full bg-emerald-50 px-2 sm:px-3 py-1 sm:py-1.5">
              <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] sm:text-xs font-semibold text-emerald-600">{isBrazil ? 'Online' : 'Active'}</span>
            </div>
            {/* Collapse/Expand Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
              aria-label={isExpanded ? (isBrazil ? 'Recolher' : 'Collapse') : (isBrazil ? 'Expandir' : 'Expand')}
            >
              <svg
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[500px] opacity-100 mt-3 sm:mt-4' : 'max-h-0 opacity-0'}`}>

          {/* Metrics Grid - Responsive: 2 cols on mobile, 4 cols on desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {automationMetrics.map((metric) => {
              const isAboveTarget = metric.unit === 's'
                ? metric.value <= metric.target
                : metric.value >= metric.target;
              return (
                <div key={metric.label} className="rounded-lg sm:rounded-xl bg-gray-50 p-2.5 sm:p-4">
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <span className="text-[10px] sm:text-xs font-medium text-gray-500 truncate pr-1">
                      <span className="sm:hidden">{metric.label}</span>
                      <span className="hidden sm:inline">{metric.labelFull}</span>
                    </span>
                    <div className="rounded-md sm:rounded-lg bg-violet-100 p-1 sm:p-1.5 text-violet-600 flex-shrink-0">
                      {metric.icon}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-0.5 sm:gap-1">
                    <span className="text-lg sm:text-2xl font-bold text-gray-900">
                      {metric.value}{metric.unit === 's' ? 's' : '%'}
                    </span>
                    <span className={`text-[9px] sm:text-xs font-medium ${isAboveTarget ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {isAboveTarget ? metric.onTarget : `${metric.target}${metric.unit || '%'}`}
                    </span>
                  </div>
                  <p className="mt-0.5 sm:mt-1 text-[9px] sm:text-xs text-gray-400 truncate">{metric.description}</p>
                </div>
              );
            })}
          </div>

          {/* AI Features Quick Actions - Simplified on mobile */}
          <div className="mt-3 sm:mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-violet-50 to-purple-50 p-2 sm:p-3">
            <div className="flex flex-wrap items-center gap-3 sm:gap-6">
              <button className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors min-h-[36px] sm:min-h-[44px]">
                <ScanIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">{t('ai.scanReceipt')}</span>
                <span className="xs:hidden">{isBrazil ? 'Scan' : 'Scan'}</span>
              </button>
              <button className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors min-h-[36px] sm:min-h-[44px]">
                <DocumentIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">{isBrazil ? 'Extrair NF-e' : t('ai.extractInvoice')}</span>
                <span className="xs:hidden">{isBrazil ? 'NF-e' : 'Extract'}</span>
              </button>
              <button className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors min-h-[36px] sm:min-h-[44px] hidden sm:flex">
                <BrainIcon className="h-4 w-4" />
                {t('ai.smartCategorize')}
              </button>
            </div>
            <span className="text-[9px] sm:text-xs text-gray-400 hidden sm:inline">{t('ai.poweredBy')}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// Icons
// ============================================================================

function SparklesIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function BrainIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  );
}

function ScanIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function TrendUpIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}

function InvoiceIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function BillIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function CashIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function LedgerIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

function BankIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l9-4 9 4v2H3V6zm0 4h18v10a2 2 0 01-2 2H5a2 2 0 01-2-2V10zm4 4v4m4-4v4m4-4v4" />
    </svg>
  );
}

function ArrowUpIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
    </svg>
  );
}

function ArrowDownIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  );
}
